import type { CSSProperties, ReactNode } from 'react'
import { Link } from 'react-router'
import { Tooltip } from '../tooltip/Tooltip'
import { textOn } from './textOn'
import styles from './LogoRail.module.css'

export interface LogoRailItem {
  id: string
  /** The team or org's name — the tooltip's title and the link's name. */
  label: string
  /** "Team · LoL · Captain", "League · Owner". Shown under the name on hover. */
  description?: string
  to: string
  logoUrl?: string
  /** A small corner badge, e.g. the team's or org's initials ("PE"), so tiles that share a logo — teams wear their club's — still tell apart. */
  badge?: string
  /** The space you're in — gets the accent bar. */
  active?: boolean
  /** With no logo: the brand colour the tile is filled with (hex). */
  color?: string
  /** With no logo: what the tile shows ("PE"). Defaults to the label's first letter. */
  initials?: string
}


export interface LogoRailProps {
  /** Groups of tiles, drawn top to bottom with a divider between groups. Empty groups are skipped. */
  groups: LogoRailItem[][]
  /** Above the tiles — the Carousel home mark, typically. */
  header?: ReactNode
  /** Below the tiles — an add button, typically. */
  footer?: ReactNode
  /** Names the landmark. Default "Your teams and organizations". */
  'aria-label'?: string
  className?: string
}

/** A logo-less tile in its brand colour, with initials picked to read on it. */
function brandStyle(color?: string): CSSProperties | undefined {
  const fg = color ? textOn(color) : undefined
  return fg ? ({ '--tile-bg': color, '--tile-fg': fg } as CSSProperties) : undefined
}

function Tile({ item }: { item: LogoRailItem }) {
  const name = item.description ? `${item.label}, ${item.description}` : item.label
  return (
    <Tooltip
      placement="right"
      delay={100}
      content={
        <span className={styles.tip}>
          <span className={styles.tipName}>{item.label}</span>
          {item.description && <span className={styles.tipMeta}>{item.description}</span>}
        </span>
      }
    >
      <Link
        to={item.to}
        className={item.active ? `${styles.tile} ${styles.active}` : styles.tile}
        aria-label={name}
        aria-current={item.active ? 'page' : undefined}
      >
        {item.logoUrl
          ? <img src={item.logoUrl} alt="" className={styles.logo} />
          : (
            <span className={item.color ? `${styles.initial} ${styles.branded}` : styles.initial} style={brandStyle(item.color)} aria-hidden="true">
              {item.initials || item.label.charAt(0).toUpperCase()}
            </span>
          )}
        {item.badge && <span className={styles.badge} aria-hidden="true">{item.badge}</span>}
      </Link>
    </Tooltip>
  )
}

/**
 * A thin strip of the teams and organizations someone belongs to, beside the
 * main nav — Discord-style. Each logo names itself on hover or focus, and the
 * space you're in gets the accent bar.
 */
export function LogoRail({ groups, header, footer, className, 'aria-label': ariaLabel = 'Your teams and organizations' }: LogoRailProps) {
  const shown = groups.filter((g) => g.length > 0)
  return (
    <nav className={className ? `${styles.rail} ${className}` : styles.rail} aria-label={ariaLabel}>
      {header && <div className={styles.header}>{header}</div>}
      <div className={styles.tiles}>
        {shown.map((group, i) => (
          <div key={i} className={styles.group}>
            {(i > 0 || header) && <span className={styles.divider} aria-hidden="true" />}
            <ul className={styles.list}>
              {group.map((item) => <li key={item.id}><Tile item={item} /></li>)}
            </ul>
          </div>
        ))}
        {footer && <div className={styles.footer}>{(shown.length > 0 || header) && <span className={styles.divider} aria-hidden="true" />}{footer}</div>}
      </div>
    </nav>
  )
}
